<template>
  <div class="single-value-display" :class="{ compact }">
    <!-- COMPACT MODE -->
    <template v-if="compact">
      <div v-if="isNumeric" class="compact-numeric">
        <span class="compact-value">{{ formattedValue }}</span>
        <span v-if="unit" class="compact-unit">{{ unit }}</span>
      </div>
      
      <div v-else class="compact-class">
        <div class="compact-indicator" :style="indicatorStyle"></div>
        <span class="compact-label">{{ formattedClassName }}</span>
      </div>
    </template>

    <!-- FULL MODE -->
    <template v-else>
      <!-- Numeric Display -->
      <div v-if="isNumeric" class="numeric-display">
        <div class="value-container">
          <span class="value-large" :style="{ color }">{{ formattedValue }}</span>
          <span v-if="unit" class="value-unit">{{ unit }}</span>
        </div>
        
        <div v-if="subtitle" class="value-subtitle">{{ subtitle }}</div>
        
        <div v-if="showChange && previousValue !== null" class="value-change" :class="changeClass">
          <q-icon :name="changeIcon" size="1rem" />
          <span>{{ changeText }}</span>
        </div>
      </div>

      <!-- Class Display -->
      <div v-else class="class-display">
        <div class="class-indicator" :style="indicatorStyle">
          <span class="class-abbrev" :class="{ 'small-text': isLongWord }">
            {{ abbreviation }}
          </span>
        </div>
        
        <div class="class-name">{{ className || formattedClassName }}</div>
        
        <div v-if="description" class="class-description">{{ description }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { getColorForClass } from '@/common/mixins/colors'

export default {
  name: 'SingleValueDisplay',
  
  props: {
    value: { type: [Number, String], required: true },
    unit: { type: String, default: null },
    decimals: { type: Number, default: 2 },
    color: { type: String, default: null },
    colorMap: { type: Object, default: null },
    className: { type: String, default: null },
    subtitle: { type: String, default: null },
    description: { type: String, default: null },
    showChange: { type: Boolean, default: false },
    previousValue: { type: Number, default: null },
    changeFormat: { type: String, default: 'percentage' },
    compact: { type: Boolean, default: false }
  },

  computed: {
    isNumeric() {
      return typeof this.value === 'number'
    },

    isLongWord() {
      return typeof this.value === 'string' && 
             this.value.split(' ').length === 1 && 
             this.value.length > 3
    },

    formattedValue() {
      if (!this.isNumeric || this.value == null) return 'N/A'
      return this.value.toFixed(this.compact ? 1 : this.decimals)
    },

    formattedClassName() {
      if (typeof this.value !== 'string') return this.value
      return this.value
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },

    abbreviation() {
      if (typeof this.value !== 'string') return this.value
      
      const words = this.value.split(' ')
      
      // Više riječi → inicijali
      if (words.length > 1) {
        return words.map(w => w.charAt(0).toUpperCase()).join('')
      }
      
      // Duga riječ (>8) → prva 3 slova
      if (this.value.length > 8) {
        return this.value.substring(0, 3).toUpperCase()
      }
      
      // Kratka riječ → capitalize
      return this.value.charAt(0).toUpperCase() + this.value.slice(1)
    },

    indicatorStyle() {
      return { backgroundColor: this.color || getColorForClass(this.value, this.colorMap) }
    },

    // Change tracking
    change() {
      return this.previousValue != null ? this.value - this.previousValue : null
    },

    changePercentage() {
      if (this.change == null || this.previousValue === 0) return null
      return (this.change / this.previousValue) * 100
    },

    changeIcon() {
      if (this.change == null) return 'remove'
      if (this.change > 0) return 'trending_up'
      if (this.change < 0) return 'trending_down'
      return 'trending_flat'
    },

    changeClass() {
      if (this.change == null) return 'neutral'
      if (this.change > 0) return 'positive'
      if (this.change < 0) return 'negative'
      return 'neutral'
    },

    changeText() {
      if (this.change == null) return this.$t('No previous data')
      
      const abs = Math.abs(this.change).toFixed(this.decimals)
      const pct = Math.abs(this.changePercentage).toFixed(1)
      const sign = this.change > 0 ? '+' : ''
      
      if (this.changeFormat === 'percentage') return `${sign}${pct}%`
      if (this.changeFormat === 'absolute') return `${sign}${abs} ${this.unit || ''}`.trim()
      return `${sign}${abs} ${this.unit || ''} (${sign}${pct}%)`.trim()
    }
  }
}
</script>

<style lang="scss" scoped>
.single-value-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  gap: 0.75rem;

  &.compact {
    padding: 0;
    gap: 0.25rem;
  }
}

// ================================
// COMPACT MODE
// ================================
.compact-numeric {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.compact-value {
  font-size: 1rem;
  font-weight: 700;
  color: $grey-9;

  body.body--dark & {
    color: $grey-3;
  }
}

.compact-unit {
  font-size: 0.625rem;
  color: $grey-6;
}

.compact-class {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.compact-indicator {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.compact-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: $grey-8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 6rem;

  body.body--dark & {
    color: $grey-4;
  }
}

// ================================
// FULL MODE - Numeric
// ================================
.numeric-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.value-container {
  text-align: center;
}

.value-large {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1;
  display: block;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.value-unit {
  display: block;
  font-size: 1.125rem;
  font-weight: 500;
  color: $grey-7;
  margin-top: 0.5rem;

  body.body--dark & {
    color: $grey-5;
  }
}

.value-subtitle {
  font-size: 0.875rem;
  color: $grey-7;
  text-align: center;

  body.body--dark & {
    color: $grey-5;
  }
}

.value-change {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 1rem;
  background: $grey-2;

  body.body--dark & {
    background: $grey-9;
  }

  &.positive { color: $positive; }
  &.negative { color: $negative; }
  &.neutral { color: $grey-6; }
}

// ================================
// FULL MODE - Class
// ================================
.class-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.class-indicator {
  width: 6.25rem;
  height: 6.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-md);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.class-abbrev {
  font-size: 2.25rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  &.small-text {
    font-size: 1.25rem;
    letter-spacing: 0;
  }
}

.class-name {
  font-size: 1rem;
  font-weight: 600;
  color: $grey-8;
  text-align: center;
  max-width: 100%;
  word-wrap: break-word;

  body.body--dark & {
    color: $grey-4;
  }
}

.class-description {
  font-size: 0.75rem;
  color: $grey-7;
  text-align: center;
  max-width: 100%;
  line-height: 1.4;

  body.body--dark & {
    color: $grey-5;
  }
}

// ================================
// RESPONSIVE
// ================================
@media (max-width: 48rem), (max-height: 48rem)  {
  .value-large {
    font-size: 2.625rem;
  }

  .value-unit {
    font-size: 1rem;
  }

  .class-indicator {
    width: 5rem;
    height: 5rem;
  }

  .class-abbrev {
    font-size: 1.75rem;
  }
}
</style>